import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { orderBy, has, set, get, split } from 'lodash/fp';
import { Table, Icon, IconButton } from 'rsuite';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { fullAccess, modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../AccessControl';
import DeleteInventoryTagModal from '../DeleteTagModal';
import EditInventoryTagModal from '../EditTagModal';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import { InventoryTagType } from '../../../interfaces/inventory';
import AutoHeightTable from '../../AutoHeightTable';

const { Column, HeaderCell, Cell } = Table;

const ActionCell = ({
  rowData,
  dataKey,
  handleDelete,
  handleEdit,
  ...props
}) => (
  <Cell {...props} className="link-group">
    <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.TAG)}>
      <IconButton
        appearance="link"
        style={{ top: '-8px' }}
        color="blue"
        icon={<Icon icon="edit2" />}
        onClick={() => handleEdit(rowData)}
      />
    </AccessControl>
    <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.TAG)}>
      <IconButton
        style={{ top: '-8px' }}
        appearance="link"
        color="red"
        onClick={() => handleDelete(rowData)}
        icon={<Icon icon="trash" />}
      />
    </AccessControl>
  </Cell>
);
ActionCell.propTypes = {
  rowData: InventoryTagType,
  dataKey: PropTypes.string.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired
};
ActionCell.defaultProps = {
  rowData: null
};

class InventoryTagList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tagToDelete: null,
      tagToEdit: null,
      showConfirmDelete: false,
      showConfirmEdit: false
    };
  }

  componentDidMount() {
    const { onInit, isFetched } = this.props;
    if (!isFetched) {
      onInit();
    }
  }

  componentDidUpdate({ isDeleted: prevIsDeleted }) {
    const { isDeleted } = this.props;
    if (!prevIsDeleted && isDeleted) {
      this.handleCloseDeleteModal();
    }
  }

  getData() {
    const { tags } = this.props;
    const { sortColumn, sortType } = this.state;
    return orderBy(sortColumn, sortType, tags);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleOpenEditModal = tagToEdit => {
    this.setState({ showConfirmEdit: true, tagToEdit });
  };

  handleOpenDeleteModal = tagToDelete => {
    this.setState({ showConfirmDelete: true, tagToDelete });
  };

  handleCloseDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handleCloseEditModal = () => {
    this.setState({ showConfirmEdit: false });
  };

  handlConfirmDelete = () => {
    const { tagToDelete } = this.state;
    const { onDelete } = this.props;
    onDelete(tagToDelete);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const { isFetching, isDeleting, query } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    const {
      tagToDelete,
      tagToEdit,
      showConfirmDelete,
      showConfirmEdit
    } = this.state;

    return (
      <React.Fragment>
        <AutoHeightTable
          reduceHeight={540}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
          bordered
        >
          <Column width={60} align="center" fixed sortable>
            <CustomHeaderCell>Id</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={180} fixed sortable>
            <CustomHeaderCell>Name</CustomHeaderCell>
            <Cell dataKey="name" />
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell>Date Created</CustomHeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell>Date Updated</CustomHeaderCell>
            <DateCell dataKey="updatedAt" />
          </Column>
          <Column width={100}>
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              align="center"
              dataKey="id"
              handleDelete={this.handleOpenDeleteModal}
              handleEdit={this.handleOpenEditModal}
            />
          </Column>
        </AutoHeightTable>

        {this.renderPagination()}

        {tagToDelete && (
          <DeleteInventoryTagModal
            show={showConfirmDelete}
            onClose={this.handleCloseDeleteModal}
            onConfirm={this.handlConfirmDelete}
            tag={tagToDelete}
            isDeleting={isDeleting}
          />
        )}
        {tagToEdit && (
          <EditInventoryTagModal
            show={showConfirmEdit}
            onClose={this.handleCloseEditModal}
            onConfirm={this.handleSaveEdit}
            tag={tagToEdit}
          />
        )}
      </React.Fragment>
    );
  }
}

InventoryTagList.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isFetched: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired
};

export default InventoryTagList;
